//
// # GenAlpha Schematic Schema
//
// v7 removes the embedded `symbols: Array<Symbol>` field that was present in
// v6. Symbols are now resolved via the depot (mirroring how layouts resolve
// child components by ident). Instance.target continues to reference its
// target symbol by `Ident`, unchanged from v6.
//

// Local Imports
import * as core from "./core";
import { SchemaVersion } from "./version";

// # Schematic
export interface Schematic {
  schemaVersion: SchemaVersion; // Schema Version
  contentKind: "schematic"; // Content Kind Discriminator
  ident: core.Ident; // Identifier
  pdkId?: string;
  instances: Array<Instance>; // Instances
  pins: Array<SchPin>; // Pins
  wireSegments: Array<WireSegment>; // Wire Segments
  graphics: Array<core.Element>; // Annotation-Only "Drawing" Graphics
  bbox?: core.Bbox; // Bounding Box
  properties?: Array<core.Property>; // Properties
  symbolImpl?: core.Ident; // Backward reference: The symbol this schematic implements
  layoutImpls?: Array<LayoutImplRef>; // Forward references: Layouts that implement this schematic
  constraints?: Constraints; // Symmetry/grouping constraints for placement
  oaResidue?: core.OaTermResidue; // OA term and signal types with no GenAlpha value
}

//
// # Schematic Constraints
// Symmetry and grouping constraints that inform layout placement.
// Stored directly on the schematic as the single source of truth.
// Layouts access these via their schematicImpl reference.
//
// TODO: Consider flattening this structure to match runtime state (SchematicDesignState).
// Currently groups contain nested matches, but runtime uses flat IdMaps with groupId references.
// This requires conversion on load (createSchematicDesign) and save (StateSerializationService).
// Flattening would eliminate this conversion and be more CRDT-friendly.
//
export interface Constraints {
  groups: Array<ConstraintGroup>;
  matches: Array<ConstraintMatch>; // Standalone matches not in a group
}

// A group of matched instance pairs with a shared symmetry axis
export interface ConstraintGroup {
  id?: core.Id;
  description?: string;
  matches?: Array<ConstraintMatch>; // Optional - in state, matches are stored in flat IdMap with groupId reference
  symmetryAxisPos?: number;
  symmetryDirection: "vertical" | "horizontal";
}

// A match pairing two sets of instances
export interface ConstraintMatch {
  id?: core.Id;
  description?: string;
  firstInstances: string[];
  secondInstances: string[];
  constraintType?: string; // e.g., "differential", "current_mirror", "matched_load"
  groupId?: core.Id; // Reference to parent group (if any)
}

//
// # Layout Implementation Reference
// Reference to a layout that implements this schematic, with optional properties
//
export interface LayoutImplRef {
  target: core.Ident; // The layout being referenced
  properties?: Array<core.Property>; // Optional properties for this reference (e.g., isDefault: true)
}

export interface SchPin {
  signalType?: core.SignalType;
  portName: string; // Port Name
  direction: core.PortDir; // Port Direction
  xform: core.Xform; // Transform
  labelFmt: core.TextFormat; // Label Format
  id?: core.Id;
}

// # Schematic Instance
export interface Instance {
  name: string; // Instance Name
  target: core.Ident; // Target Instantiable's Identifier
  xform: core.Xform; // Transform
  params?: Array<InstanceParam>; // Parameters
  id?: core.Id;
  symbolType?: BuiltinSymbolType; // Builtin symbol type for device classification (from PDK symbolTypeMap)
}

// # Instance Parameter
export type InstanceParam = InstanceParamObject | InstanceParamTuple;

// # Instance Parameter
// As an object
export interface InstanceParamObject {
  name: string; // Parameter Name
  value: string; // Stringified Value
  kind?: ParamKind; // Explicit Type. Defaults to string.
}

// # Instance Parameter Tuple
// E.g. ["nfin", "4", "int"]
// Note while typescript allows an optional third element syntax,
// JSON, or at least our JSON parser, does not support it.
export type InstanceParamTuple = InstParamTuple2 | InstParamTuple3;
type InstParamTuple2 = [name: string, value: string];
type InstParamTuple3 = [name: string, value: string, kind: ParamKind];

export type ParamKind = "string" | "bool" | "float" | "int";

/**
 * Builtin symbol types for device classification.
 * Used for symmetry inference and other analysis.
 * Maps to standard analog/mixed-signal circuit element types.
 */
export type BuiltinSymbolType =
  | "nmos"
  | "pmos"
  | "res"
  | "res3"
  | "cap"
  | "cap3"
  | "tap"
  | "npn"
  | "pnp"
  | "diode"
  | "ind"
  | "rect"
  | "ccs"
  | "gnd"
  | "vdd"
  | "vdc";

// # Wire Segment
export interface WireSegment {
  endpoints: [core.Point, core.Point]; // Endpoints
  style: core.PathStyle; // Wire / Path Style
  labels: Array<core.Text>; // Labels
  id?: core.Id;
}

export type SchematicPin = SchPin;
